import { Alert, Modal, Select } from '@acin/ui-core';
import { SelectOption, SelectOptionValue } from '@acin/ui-core/types/src/components/Select/components/Select';
import React from 'react';
import {
  IInternalMappingsJob,
  IInternalMappingsJobStatus,
  IInternalRegulationReviewStatus,
} from '../../../graphql/generated';
import { useClientsQuery } from '../../../graphql/queries/getClients.generated';
import { useRegulationsQuery } from '../../../graphql/queries/getRegulations.generated';
import { useCreateInternalMappingsJobMutation } from '../graphql/mutations/createInternalMappingsJob.generated';
import * as SC from './CreateMappingsJobModal.styled';

export interface CreateMappingsJobModalProps {
  show: boolean;
  onClose: () => void;
  existingJobs: Array<Pick<IInternalMappingsJob, 'clientCode' | 'regulationId' | 'status'>>;
}

export const CreateMappingsJobModal: React.FC<CreateMappingsJobModalProps> = (props) => {
  const { show, onClose, existingJobs } = props;

  const [selectedClientCode, setSelectedClientCode] = React.useState<string | undefined>(undefined);
  const [selectedRegulationId, setSelectedRegulationId] = React.useState<string | undefined>(undefined);

  // Always open with a blank selection, regardless of what was picked last time.
  React.useEffect(() => {
    if (show) {
      setSelectedClientCode(undefined);
      setSelectedRegulationId(undefined);
    }
  }, [show]);

  const { data: clientsData } = useClientsQuery({ variables: { first: 100 }, skip: !show });
  const { data: regulationsData } = useRegulationsQuery({
    variables: {
      first: 100,
      input: {
        filters: {
          groups: [
            {
              groupKey: 'REGULATION_STATUS',
              filterValues: [
                {
                  value: IInternalRegulationReviewStatus.Reviewed,
                },
              ],
            },
          ],
        },
      },
    },
    skip: !show,
  });
  const [createMappingsJob, { loading, error }] = useCreateInternalMappingsJobMutation();

  const clientOptions: SelectOption[] = (clientsData?.clients?.edges ?? []).map((edge) => ({
    value: edge.node.code,
    label: edge.node.shortName ? `${edge.node.name} (${edge.node.shortName})` : `${edge.node.name} (${edge.node.code})`,
  }));

  const regulationOptions: SelectOption[] = (regulationsData?.internalRegulations?.edges ?? [])
    .filter((edge) => !!edge.node.id)
    .map((edge) => ({
      value: edge.node.id as string,
      label: edge.node.title,
    }));

  const hasConflictingInProgressJob = existingJobs.some(
    (job) =>
      job.clientCode === selectedClientCode &&
      job.regulationId === selectedRegulationId &&
      job.status === IInternalMappingsJobStatus.InProgress,
  );

  const handleClientChange = (value: SelectOptionValue) => setSelectedClientCode(String(value));
  const handleRegulationChange = (value: SelectOptionValue) => setSelectedRegulationId(String(value));

  const handleCreate = async () => {
    if (!selectedClientCode || !selectedRegulationId) return;

    const { errors } = await createMappingsJob({
      variables: { input: { clientCode: selectedClientCode, regulationId: selectedRegulationId } },
      errorPolicy: 'all',
      refetchQueries: ['internalMappingsJobs'],
      awaitRefetchQueries: true,
    });

    if (errors?.length) return;

    onClose();
  };

  return (
    <Modal
      show={show}
      title="Create New Client Mappings"
      size="medium"
      onClose={onClose}
      footerButtonPrimary={{
        label: 'Create',
        onClick: handleCreate,
        disabled: !selectedClientCode || !selectedRegulationId || loading || hasConflictingInProgressJob,
      }}
    >
      <SC.CreateMappingsJobModal data-testid="create-mappings-job-modal">
        {error && <Alert status="error" title="Could not create mappings job" />}
        {hasConflictingInProgressJob && (
          <Alert status="warning" title="A mapping job for this client and regulation is already in progress" />
        )}
        <Select
          onChange={handleClientChange}
          options={clientOptions}
          value={selectedClientCode ?? null}
          placeholder="Select a client"
          prefix="Client"
          data-testid="create-mappings-job-client-select"
        />
        <Select
          onChange={handleRegulationChange}
          options={regulationOptions}
          value={selectedRegulationId ?? null}
          placeholder="Select a regulation"
          prefix="Regulation"
          data-testid="create-mappings-job-regulation-select"
        />
      </SC.CreateMappingsJobModal>
    </Modal>
  );
};
